// Anonymous analytics emitter. Two tiers of consent, both anonymous; one
// server-side log that never sees client identifiers.
//
// Tier 0 — search misses. Server-side rows with the query text only. We post
//   them through the same Supabase client (RLS allows insert by anon role).
//
// Tier 1 — usage events (default ON, opt-out). Tap-level analytics keyed by a
//   per-install random uuid stored in localStorage. NEVER tied to user_id.
//
// Tier 2 — research observations (opt-in). Rich data: doses, biometrics,
//   stack composition. Uses a SEPARATE per-install random uuid so the two
//   tiers cannot be cross-referenced.

import { supabase } from './supabase';

const K_TIER1 = 'mystack:analytics:tier1:v1';        // boolean toggle (default false; opt-in via first-launch prompt or Profile)
const K_TIER2 = 'mystack:analytics:tier2:v1';        // boolean toggle (default false)
const K_ANON1 = 'mystack:analytics:anon1:v1';        // per-install uuid for Tier 1
const K_ANON2 = 'mystack:analytics:anon2:v1';        // per-install uuid for Tier 2
const K_CONSENT_PROMPTED = 'mystack:analytics:consent-prompted:v1';

// Cryptographically-random RFC 4122 v4 UUID. Prefers crypto.randomUUID(),
// falls back to crypto.getRandomValues() — never Math.random.
export function uuidV4(): string {
  if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID();
  const b = new Uint8Array(16);
  // crypto.getRandomValues is mandatory on all modern browsers + Node 18+.
  (crypto as any).getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `${hex(b[0])}${hex(b[1])}${hex(b[2])}${hex(b[3])}-${hex(b[4])}${hex(b[5])}-${hex(b[6])}${hex(b[7])}-${hex(b[8])}${hex(b[9])}-${hex(b[10])}${hex(b[11])}${hex(b[12])}${hex(b[13])}${hex(b[14])}${hex(b[15])}`;
}

function getAnonId(key: string): string {
  let id = localStorage.getItem(key);
  if (!id) {
    id = uuidV4();
    localStorage.setItem(key, id);
  }
  return id;
}

/* ---------- Consent toggles ---------- */

export function isTier1Enabled(): boolean {
  // Default OFF — opt-in only. First-launch prompt asks; Profile toggles thereafter.
  return localStorage.getItem(K_TIER1) === 'true';
}

export function isConsentPrompted(): boolean {
  return localStorage.getItem(K_CONSENT_PROMPTED) === 'true';
}
export function markConsentPrompted(): void {
  localStorage.setItem(K_CONSENT_PROMPTED, 'true');
}
export function setTier1(v: boolean): void {
  localStorage.setItem(K_TIER1, v ? 'true' : 'false');
}

export function isTier2Enabled(): boolean {
  return localStorage.getItem(K_TIER2) === 'true'; // default OFF
}
export function setTier2(v: boolean): void {
  localStorage.setItem(K_TIER2, v ? 'true' : 'false');
}

/* ---------- Emitters ---------- */

/** Tier 0 — no consent gate. Server-side miss log for catalog gap signal. */
export async function logSearchMiss(query: string): Promise<void> {
  if (!query || query.length < 2) return;
  try {
    await supabase.from('mystack_search_misses').insert({ query });
  } catch { /* silent — analytics never blocks UX */ }
}

/** Tier 1 — tap analytics. Skipped when the user has toggled it off. */
export async function trackEvent(eventType: string, payload?: Record<string, unknown>): Promise<void> {
  if (!isTier1Enabled()) return;
  try {
    await supabase.from('mystack_anon_events').insert({
      anon_id: getAnonId(K_ANON1),
      event_type: eventType,
      payload: payload ?? null,
    });
  } catch { /* silent */ }
}

/** Tier 2 — research observation. Strict opt-in. */
export async function recordObservation(opts: {
  kind: 'dose' | 'biometric' | 'wellbeing' | 'stack_composition' | 'fasting' | 'cycle';
  value?: number;
  unit?: string;
  compound_id?: string;
  payload?: Record<string, unknown>;
}): Promise<void> {
  if (!isTier2Enabled()) return;
  try {
    await supabase.from('mystack_anon_observations').insert({
      anon_id: getAnonId(K_ANON2),
      kind: opts.kind,
      value: opts.value ?? null,
      unit: opts.unit ?? null,
      compound_id: opts.compound_id ?? null,
      payload: opts.payload ?? null,
    });
  } catch { /* silent */ }
}
